import type React from 'react';
import { CheckIcon, ClipboardIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { useToast } from '../../providers/ToastProvider';

const COPY_FAILED_MESSAGE = "Couldn't copy to the clipboard.";

interface CopyTextButtonProps {
    text: string;
    label: string;
    // Lets a row hide the idle icon until hover; the button stays visible on focus and once copied.
    idleClassName?: string;
}

// Clicking a Radix tooltip trigger closes the tooltip, so "Copied" shows inline instead.
export function CopyTextButton({ text, label, idleClassName = '' }: CopyTextButtonProps) {
    const { copied, copy } = useCopyToClipboard();
    const { showToast } = useToast();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.preventDefault();
        e.stopPropagation();
        void copy(text).then((ok) => {
            if (!ok) showToast(COPY_FAILED_MESSAGE, 'error');
        });
    };

    return (
        <span className="inline-flex items-center flex-shrink-0">
            <Tooltip content={label} side="bottom">
                <button
                    type="button"
                    onClick={handleClick}
                    aria-label={label}
                    className={`inline-flex items-center justify-center p-0.5 rounded transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-400 focus-visible:opacity-100 ${
                        copied
                            ? 'text-green-500'
                            : `text-gray-400 hover:text-gray-700 ${idleClassName}`
                    }`}
                >
                    {copied ? (
                        <CheckIcon className="w-3.5 h-3.5" aria-hidden="true" />
                    ) : (
                        <ClipboardIcon className="w-3.5 h-3.5" aria-hidden="true" />
                    )}
                </button>
            </Tooltip>
            <span role="status" className="text-[11px] font-medium text-green-600 leading-none">
                {copied && <span className="ml-1">Copied</span>}
            </span>
        </span>
    );
}
